import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MapPinIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { incidentService, Continent, IncidentCount, IncidentByCategory, RecentIncident, THREAT_COLORS } from '../services/incidents';
import { geography } from '../services/supabase';
import { Country, State, LocalGovernmentArea } from '../types/geography';
import IncidentMapContainer from '../components/maps/IncidentMapContainer';
import toast from 'react-hot-toast';
import canadaProvincesData from '../data/canada_provinces.json';

interface Selection {
  continent: Continent | null;
  country: Country | null;
  state: State | null;
  lga: LocalGovernmentArea | null;
}

const SecurityIncidentMapPage: React.FC = () => {
  const location = useLocation();
  
  // Selection state - initialize with passed state from homepage if available
  const [selection, setSelection] = useState<Selection>(() => {
    if (location.state?.fromHomepage && location.state?.selection) {
      return location.state.selection;
    }
    return {
      continent: null,
      country: null,
      state: null,
      lga: null
    };
  });

  // Data state
  const [continents, setContinents] = useState<Continent[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [lgas, setLgas] = useState<LocalGovernmentArea[]>([]);

  // Incident data
  const [incidentCounts, setIncidentCounts] = useState<IncidentCount[]>([]);
  const [incidentsByCategory, setIncidentsByCategory] = useState<IncidentByCategory[]>([]);
  const [recentIncidents, setRecentIncidents] = useState<RecentIncident[]>([]);
  const [totalIncidents, setTotalIncidents] = useState<number>(0);

  // UI state
  const [loading, setLoading] = useState(true);
  const [mapLevel, setMapLevel] = useState<'country' | 'state' | 'lga'>('country');

  // Get current area name for display
  const getCurrentAreaName = useCallback(() => {
    if (selection.lga) return selection.lga.name;
    if (selection.state) return selection.state.name;
    if (selection.country) return selection.country.name;
    if (selection.continent) return selection.continent.name;
    return 'All Regions';
  }, [selection]);

  // Load continents on mount and pre-select Canada if no selection from homepage
  useEffect(() => {
    const loadContinents = async () => {
      const { data, error } = await incidentService.getContinents();
      if (error) {
        toast.error('Failed to load continents');
        return;
      }
      setContinents(data || []);
      
      // If no selection from homepage, pre-select Canada
      if (!location.state?.fromHomepage) {
        // Find North America continent
        const northAmerica = data?.find((c: Continent) => c.name === 'North America');
        if (northAmerica) {
          setSelection(prev => ({ ...prev, continent: northAmerica }));
          
          // Load countries for North America and pre-select Canada
          const countriesResult = await incidentService.getCountriesByContinent(northAmerica.id);
          if (countriesResult.data) {
            const canada = countriesResult.data.find((c: Country) => c.code === 'CAN');
            if (canada) {
              setSelection(prev => ({ ...prev, continent: northAmerica, country: canada }));
            }
          }
        }
      }
      
      setLoading(false);
    };
    loadContinents();
  }, [location.state]);

  // Load countries when continent changes
  useEffect(() => {
    if (!selection.continent) {
      setCountries([]);
      return;
    }

    const loadCountries = async () => {
      const { data, error } = await incidentService.getCountriesByContinent(selection.continent!.id);
      if (error) {
        toast.error('Failed to load countries');
        return;
      }
      setCountries(data || []);
    };
    loadCountries();
  }, [selection.continent]);

  // Load states when country changes
  useEffect(() => {
    if (!selection.country) {
      setStates([]);
      return;
    }

    const loadStates = async () => {
      const { data, error } = await geography.getStates(selection.country!.id);
      if (error) {
        // Log error but don't show toast - some countries may not have states in DB
        console.log(`No state data available for ${selection.country!.name}`);
        setStates([]);
        setMapLevel('country');
        return;
      }
      
      let loadedStates = data || [];

      // Merge Canadian boundaries if selected country is Canada
      if (selection.country!.code === 'CAN' || selection.country!.name === 'Canada') {
        const geoFeatures = (canadaProvincesData as any).features;
        const combinedStates: State[] = [];
        const processedDbStates = new Set<string>();

        // Helper to normalize names for matching
        const normalize = (s: string) => s.toLowerCase().replace(/territory|province/g, '').trim();

        // 1. Map existing DB states to geometries
        loadedStates = loadedStates.map((state: State) => {
          const feature = geoFeatures.find((f: any) => 
            normalize(f.properties.name) === normalize(state.name)
          );

          if (feature) {
            processedDbStates.add(normalize(state.name));
            return {
              ...state,
              geometry: feature.geometry
            };
          }
          return state;
        });

        // 2. Add missing provinces from GeoJSON as virtual states
        geoFeatures.forEach((feature: any) => {
          const name = feature.properties.name;
          if (!processedDbStates.has(normalize(name))) {
            loadedStates.push({
              id: `virtual-${name.replace(/\s+/g, '-')}`,
              country_id: selection.country!.id,
              name: name,
              geometry: feature.geometry,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            } as State);
          }
        });
      }

      setStates(loadedStates);
      setMapLevel('state');
    };
    loadStates();
  }, [selection.country]);

  // Load LGAs when state changes
  useEffect(() => {
    if (!selection.state) {
      setLgas([]);
      return;
    }

    const loadLgas = async () => {
      // Skip LGA loading for virtual states (they don't exist in the database)
      if (selection.state!.id.startsWith('virtual-')) {
        setLgas([]);
        setMapLevel('state');
        return;
      }

      try {
        const { data, error } = await geography.getLGAs(selection.state!.id);
        
        // Handle errors gracefully - missing LGA data is expected for many regions
        if (error) {
          console.log(`No LGA data available for ${selection.state!.name}:`, error);
          setLgas([]);
          setMapLevel('state');
          return;
        }
        
        const loadedLgas = data || [];
        
        // Filter out LGAs without valid geometry to prevent rendering issues
        // Validate that geometry is proper GeoJSON with type and coordinates
        const validLgas = loadedLgas.filter((lga: LocalGovernmentArea) => {
          if (!lga.geometry) {
            return false;
          }
          // Ensure it's valid GeoJSON with required properties
          if (typeof lga.geometry !== 'object') {
            return false;
          }
          // Check for GeoJSON structure: must have type and coordinates
          const geom = lga.geometry as any;
          if (!geom.type || !geom.coordinates) {
            return false;
          }
          return true;
        });
        
        console.log(`Loaded ${loadedLgas.length} LGAs, ${validLgas.length} have valid geometry for ${selection.state!.name}`);
        
        // Deduplicate LGAs based on normalized name to prevent duplicates in dropdown
        const uniqueLgas = validLgas.reduce((acc: LocalGovernmentArea[], current: LocalGovernmentArea) => {
          const isDuplicate = acc.some(item => 
            item.name.toLowerCase().trim() === current.name.toLowerCase().trim()
          );
          if (!isDuplicate) {
            acc.push(current);
          }
          return acc;
        }, []);
        
        setLgas(uniqueLgas);
        
        // Switch to LGA level only if we have valid LGAs with geometry
        if (uniqueLgas.length > 0) {
          console.log(`✅ Switching to city-level map with ${uniqueLgas.length} LGAs`);
          setMapLevel('lga');
        } else {
          // No valid LGAs available, stay at state level
          console.log(`❌ No valid LGAs with geometry, staying at state level (zoom only)`);
          setMapLevel('state');
        }
      } catch (err) {
        console.error(`Error loading LGAs for ${selection.state!.name}:`, err);
        setLgas([]);
        setMapLevel('state');
      }
    };
    loadLgas();
  }, [selection.state]);

  // Load incident data based on selection and map level
  useEffect(() => {
    const loadIncidentData = async () => {
      try {
        // 1. Map Coloring Data
        // Determine what level of data to fetch for the map visualization
        // This depends on what is actually being displayed (mapLevel)
        let mapScopeLevel = mapLevel;
        let mapScopeId: string | undefined;

        if (mapScopeLevel === 'lga') {
          mapScopeId = selection.state?.id;
        } else if (mapScopeLevel === 'state') {
          mapScopeId = selection.country?.id;
        } else {
          mapScopeId = selection.continent?.id;
        }

        // Load incident counts for map coloring
        const countsResult = await incidentService.getIncidentCounts(mapScopeLevel, mapScopeId);
        if (countsResult.error) {
          console.error('Error loading incident counts:', countsResult.error);
          setIncidentCounts([]);
        } else if (countsResult.data) {
          setIncidentCounts(countsResult.data);
        } else {
          setIncidentCounts([]);
        }

        // 2. Sidebar/Detail Data
        // Query at the SAME level as selection to get aggregate incident data
        // The SQL function filters by the specific area ID at that level
        let detailLevel: 'country' | 'state' | 'lga' = 'country';
        let detailId: string | undefined;

        if (selection.lga) {
          detailLevel = 'lga';
          detailId = selection.lga.id;
        } else if (selection.state) {
          detailLevel = 'state';
          detailId = selection.state.id;
        } else if (selection.country) {
          // Query country-level incidents for this specific country
          detailLevel = 'country';
          detailId = selection.country.id;
        } else if (selection.continent) {
          // Query all country-level incidents (no continent filter in SQL)
          // We'll get global data, but that's the best we can do without continent_id in incidents table
          detailLevel = 'country';
          detailId = undefined;
        }

        // Load incidents by category for sidebar
        const categoryResult = await incidentService.getIncidentsByCategory(
          detailLevel,
          detailId
        );
        if (categoryResult.data) {
          // Sort to ensure 'Other' is always last
          const sortedCategories = [...categoryResult.data].sort((a, b) => {
            if (a.category_name.toLowerCase() === 'other') return 1;
            if (b.category_name.toLowerCase() === 'other') return -1;
            return b.incident_count - a.incident_count; // Sort by count descending for others
          });
          setIncidentsByCategory(sortedCategories);
          setTotalIncidents(categoryResult.data.reduce((sum: number, cat: IncidentByCategory) => sum + cat.incident_count, 0));
        }

        // Load recent incidents
        const recentResult = await incidentService.getRecentIncidents(
          detailLevel,
          detailId,
          10
        );
        if (recentResult.data) {
          // Sort recent incidents to put 'Other' category at the bottom
          // while maintaining date sort for the rest
          const sortedIncidents = [...recentResult.data].sort((a, b) => {
            const isOtherA = a.category.toLowerCase() === 'other';
            const isOtherB = b.category.toLowerCase() === 'other';
            
            if (isOtherA && !isOtherB) return 1;
            if (!isOtherA && isOtherB) return -1;
            
            // If both are 'Other' or both are not 'Other', keep original chronological order (assumed from API)
            // Or explicitly sort by date descending
            return new Date(b.incident_date).getTime() - new Date(a.incident_date).getTime();
          });
          setRecentIncidents(sortedIncidents);
        }
      } catch (err) {
        console.error('Error loading incident data:', err);
        setIncidentCounts([]);
        setIncidentsByCategory([]);
        setRecentIncidents([]);
      }
    };

    loadIncidentData();
  }, [selection, mapLevel]);

  // Handle selection changes
  const handleContinentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const continent = continents.find(c => c.id === e.target.value) || null;
    setSelection({ continent, country: null, state: null, lga: null });
    setMapLevel('country');
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const country = countries.find(c => c.id === e.target.value) || null;
    setSelection(prev => ({ ...prev, country, state: null, lga: null }));
  };

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const state = states.find(s => s.id === e.target.value) || null;
    
    // Note: We do NOT update inheritedThreatLevel here because we are keeping 
    // the map at 'state' level (showing all provinces), so the fallback color 
    // should remain the Country's threat level.
    
    setSelection(prev => ({ ...prev, state, lga: null }));
  };

  const handleLgaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const lga = lgas.find(l => l.id === e.target.value) || null;
    setSelection(prev => ({ ...prev, lga }));
  };

  // Handle map feature clicks - populate form and zoom
  const handleMapClick = (feature: { id: string; name: string }) => {
    // Determine what level we're interacting with based on the MAP LEVEL,
    // not the current selection. This allows switching states even when one is selected.
    
    if (mapLevel === 'country') {
      const country = countries.find(c => c.id === feature.id);
      if (country) {
        setSelection(prev => ({ ...prev, country, state: null, lga: null }));
      }
    } else if (mapLevel === 'state') {
      const state = states.find(s => s.id === feature.id);
      if (state) {
        setSelection(prev => ({ ...prev, state, lga: null }));
      }
    } else if (mapLevel === 'lga') {
      const lga = lgas.find(l => l.id === feature.id);
      if (lga) {
        setSelection(prev => ({ ...prev, lga }));
      }
    }
  };

  // Get threat level color from incident count
  const getThreatLevelColor = (count: number): string => {
    if (count === 0) return THREAT_COLORS[1];
    if (count <= 2) return THREAT_COLORS[3];
    if (count <= 5) return THREAT_COLORS[6];
    if (count <= 10) return THREAT_COLORS[9];
    return THREAT_COLORS[12];
  };

  // Get color based on category severity (inherent danger level, not count)
  // Based on security literature threat categorization
  const getCategorySeverityColor = (categoryName: string): string => {
    const category = categoryName.toLowerCase().replace(/\s+/g, '_');
    switch (category) {
      // Extreme threat (black/dark red) - terrorism, mass violence
      case 'terrorism':
      case 'bombing':
      case 'mass_murder':
        return THREAT_COLORS[12];
      // Very high threat (dark red)
      case 'banditry':
      case 'communal_clash':
        return THREAT_COLORS[11];
      // High threat (red)
      case 'kidnapping':
      case 'human_trafficking':
      case 'murder':
        return THREAT_COLORS[10];
      // Elevated threat (orange-red)
      case 'piracy':
      case 'gun_violence':
      case 'armed_robbery':
        return THREAT_COLORS[8];
      // Moderate threat (orange)
      case 'cultism':
      case 'mob_action':
      case 'arson':
      case 'rape':
        return THREAT_COLORS[6];
      // Low-moderate threat (yellow)
      case 'assault':
      case 'theft':
      case 'drug_trafficking':
        return THREAT_COLORS[4];
      // Low threat (light green/yellow)
      case 'protest':
      case 'vandalism':
        return THREAT_COLORS[3];
      // Minimal threat (green)
      case 'fraud':
      case 'cyber_crime':
        return THREAT_COLORS[2];
      // Other/unknown - neutral
      case 'other':
      default:
        return THREAT_COLORS[4];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading security incident map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <MapPinIcon className="h-8 w-8 text-primary-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Security Incident Map</h1>
                <p className="text-sm text-gray-500">Real-time security intelligence across regions</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: getThreatLevelColor(totalIncidents) }}>
                  {totalIncidents}
                </div>
                <div className="text-xs text-gray-500">incidents (6 months)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Form */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-gray-100 p-4 rounded-lg shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Continent Select */}
              <div>
                <label htmlFor="continent" className="block text-sm font-medium text-gray-700">
                  Continent
                </label>
                <select
                  id="continent"
                  value={selection.continent?.id || ''}
                  onChange={handleContinentChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                >
                  <option value="">Select Continent</option>
                  {continents.map(continent => (
                    <option key={continent.id} value={continent.id}>
                      {continent.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Country Select */}
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                  Country
                </label>
                <select
                  id="country"
                  value={selection.country?.id || ''}
                  onChange={handleCountryChange}
                  disabled={!selection.continent}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md disabled:bg-gray-200"
                >
                  <option value="">Select Country</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* State Select */}
              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                  State/Province
                </label>
                <select
                  id="state"
                  value={selection.state?.id || ''}
                  onChange={handleStateChange}
                  disabled={!selection.country}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md disabled:bg-gray-200"
                >
                  <option value="">Select State</option>
                  {states.map(state => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* LGA Select */}
              <div>
                <label htmlFor="lga" className="block text-sm font-medium text-gray-700">
                  LGA/City
                </label>
                <select
                  id="lga"
                  value={selection.lga?.id || ''}
                  onChange={handleLgaChange}
                  disabled={!selection.state}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md disabled:bg-gray-200"
                >
                  <option value="">Select LGA</option>
                  {lgas.map(lga => (
                    <option key={lga.id} value={lga.id}>
                      {lga.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Map Section */}
          <div className="lg:col-span-2 space-y-4">
            {/* Map Container */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="px-4 py-3 border-b bg-gradient-to-r from-gray-50 to-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Current View</span>
                    <h3 className="text-lg font-semibold text-gray-900">{getCurrentAreaName()}</h3>
                  </div>
                  <div className="text-xs text-gray-500">
                    Click any region to explore
                  </div>
                </div>
              </div>
              
              <IncidentMapContainer
                level={mapLevel}
                countries={countries}
                states={states}
                lgas={lgas}
                incidentCounts={incidentCounts}
                selectedCountry={selection.country}
                selectedState={selection.state}
                selectedLga={selection.lga}
                onFeatureClick={handleMapClick}
                className="w-full h-[550px]"
              />
            </div>

            {/* Threat Level Legend */}
            <div className="bg-white rounded-lg shadow-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900">Threat Level Scale</h4>
                <span className="text-xs text-gray-500">Based on incident frequency & severity</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="flex-1 h-8 rounded-lg" style={{
                    background: `linear-gradient(to right, ${THREAT_COLORS[1]}, ${THREAT_COLORS[3]}, ${THREAT_COLORS[6]}, ${THREAT_COLORS[9]}, ${THREAT_COLORS[12]})`
                  }}></div>
                </div>
                <div className="flex justify-between text-xs text-gray-600 px-1">
                  <span className="font-medium">Minimal (1)</span>
                  <span>Low (3)</span>
                  <span>Moderate (6)</span>
                  <span>Elevated (9)</span>
                  <span className="font-medium">Critical (12)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Incident Summary */}
          <div className="lg:col-span-1 space-y-4">
            {/* Incident Categories */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="px-4 py-3 border-b bg-gradient-to-r from-primary-50 to-primary-100">
                <h3 className="text-sm font-bold text-primary-900">
                  Incident Categories
                </h3>
                <p className="text-xs text-primary-600 mt-0.5">{getCurrentAreaName()} • Past 6 months</p>
              </div>
              
              <div className="p-4">
                {incidentsByCategory.length > 0 ? (
                  <div className="space-y-2">
                    {incidentsByCategory.map((category, index) => {
                      // Color based on category severity, not count
                      const severityColor = getCategorySeverityColor(category.category_name);
                      return (
                        <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex items-center flex-1 min-w-0">
                            <div className="w-1 h-8 rounded-full mr-3" style={{ backgroundColor: severityColor }}></div>
                            <span className="text-sm font-medium text-gray-900 truncate">{category.category_name}</span>
                          </div>
                          <div className="ml-3 flex items-center space-x-2">
                            <span className="text-lg font-bold" style={{ color: severityColor }}>
                              {category.incident_count}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ExclamationTriangleIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No incidents recorded</p>
                    <p className="text-xs text-gray-400 mt-1">This area is currently clear</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Incidents */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="px-4 py-3 border-b bg-gradient-to-r from-orange-50 to-red-50">
                <h3 className="text-sm font-bold text-gray-900">Recent Activity</h3>
                <p className="text-xs text-gray-600 mt-0.5">Latest security incidents</p>
              </div>
              
              <div className="divide-y max-h-96 overflow-y-auto">
                {recentIncidents.length > 0 ? (
                  recentIncidents.map((incident, index) => {
                    const severityColor = incident.severity >= 4 ? THREAT_COLORS[10] : 
                                         incident.severity >= 3 ? THREAT_COLORS[6] : THREAT_COLORS[3];
                    return (
                      <div key={index} className="p-3 hover:bg-gray-50 transition-colors cursor-pointer">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: severityColor }}></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 leading-snug">
                              {incident.title}
                            </p>
                            <div className="flex items-center mt-1.5 text-xs text-gray-500 space-x-2">
                              <span className="px-2 py-0.5 bg-gray-100 rounded">{incident.category}</span>
                              <span>•</span>
                              <span>{incident.location_name || 'Unknown'}</span>
                            </div>
                            <div className="mt-1 text-xs text-gray-400">
                              {new Date(incident.incident_date).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric' 
                              })}
                              {incident.source_name && (
                                <span className="ml-2">via {incident.source_name}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <MapPinIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No recent incidents</p>
                    <p className="text-xs text-gray-400 mt-1">All quiet in this region</p>
                  </div>
                )}
              </div>
            </div>

            {/* Data Source Info */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
              <div className="flex items-start space-x-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-blue-900 mb-1">Data Intelligence</p>
                  <p className="text-xs text-blue-700 leading-relaxed">
                    Incidents aggregated from verified news sources and official reports. 
                    Updated near real-time via an automated AI-powered web crawler that monitors thousands of sources globally.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityIncidentMapPage;
